import { useEffect, useId, useMemo, useRef, useState } from "react";
import { pathLabel } from "@iltocat/shared";
import { ancestorIds, matchLocations } from "../lib/locationMoves.js";
import type { Taxonomy } from "../lib/useTaxonomy.js";
import { Icon } from "./Icon.js";
import { LocationTree } from "./LocationTree.js";
import { Notice } from "./Notice.js";

/**
 * Choosing where things go, when the answer is one tap: the Move dialog and
 * the sort screen both use it.
 *
 * Recents first, as big targets, because the next thing usually goes where the
 * last one did. Then a search that narrows the tree rather than replacing it
 * with a flat list, so two bins called "Bin 3" are told apart by where they
 * sit. Then the tree itself, for browsing. A location that does not exist yet
 * can be made from the search box, at the top level or inside the most recent
 * one -- the same two offers the capture picker makes, for the same reason.
 */
export function LocationChooser({
  taxonomy,
  here,
  onChoose,
  autoFocus = false,
  disabled = false,
  offerUnfiled = true,
  unfiledLabel = "Not filed anywhere",
  hidden,
}: {
  /** Offer "Not filed anywhere" as a destination. The sort screen does not: filing is its whole job. */
  offerUnfiled?: boolean;
  /** What choosing null means here: "Not filed anywhere" for items, "Top level" for a location. */
  unfiledLabel?: string;
  /** Locations that cannot be chosen and are not shown: a location being moved, and everything below it. */
  hidden?: ReadonlySet<string>;
  taxonomy: Taxonomy;
  /** Where the things being moved already are, if they share one. */
  here?: string | null | undefined;
  onChoose: (to: string | null) => void;
  autoFocus?: boolean;
  disabled?: boolean;
}) {
  const id = useId();
  const [typed, setTyped] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const input = useRef<HTMLInputElement>(null);

  // Opened as far as the place things are now, so "somewhere near here" is on screen.
  const [open, setOpen] = useState<Set<string>>(
    () => new Set(here ? [...ancestorIds(taxonomy.byId, here), here] : []),
  );

  // A phone's keyboard would cover the recents, which are the fast path there.
  useEffect(() => {
    if (autoFocus && window.matchMedia("(pointer: fine)").matches) input.current?.focus();
  }, [autoFocus]);

  const match = useMemo(() => matchLocations(taxonomy.tree, typed), [taxonomy.tree, typed]);
  const name = typed.trim().replace(/\s+/g, " ");
  const exists = taxonomy.locations.some((location) => location.name.toLowerCase() === name.toLowerCase());
  const allowed = (location: string) => !hidden?.has(location);
  const recents = taxonomy.recentLocations.filter((recent) => allowed(recent.id));
  const hits = match?.hits.filter(allowed) ?? [];
  const inside = recents[0] ?? null;
  const insideNode = inside ? taxonomy.byId.get(inside.id) : undefined;

  async function create(parentId: string | null) {
    setBusy(true);
    setError(null);
    try {
      const made = await taxonomy.addLocation(name, parentId);
      if (made) onChoose(made.id);
    } catch (problem) {
      setError(problem instanceof Error ? problem.message : "That location could not be added.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="chooser">
      <label className="sr-only" htmlFor={id}>
        Find a location
      </label>
      <div className="chooser-search">
        <Icon name="search" size={16} />
        <input
          id={id}
          ref={input}
          className="input"
          type="search"
          autoComplete="off"
          placeholder="Find or add a location"
          value={typed}
          disabled={disabled}
          onChange={(event) => setTyped(event.target.value)}
          onKeyDown={(event) => {
            if (event.key !== "Enter") return;
            event.preventDefault();
            const first = hits[0];
            if (first) onChoose(first);
            else if (name && !exists) void create(null);
          }}
        />
      </div>

      {error && <Notice tone="error">{error}</Notice>}

      {!match && recents.length > 0 && (
        <div className="chooser-recent" aria-label="Recent locations">
          {recents.map((recent) => {
            const node = taxonomy.byId.get(recent.id);
            return (
              <button
                key={recent.id}
                type="button"
                className="chooser-quick"
                disabled={disabled || recent.id === here}
                onClick={() => onChoose(recent.id)}
              >
                <Icon name="clock" size={14} />
                <span>{node ? pathLabel(node.path) : recent.name}</span>
              </button>
            );
          })}
        </div>
      )}

      <div className="chooser-tree">
        <LocationTree
          tree={taxonomy.tree}
          here={here}
          open={open}
          match={match}
          {...(offerUnfiled ? { unfiled: { label: unfiledLabel } } : {})}
          {...(hidden ? { hidden } : {})}
          onToggle={(node, now) =>
            setOpen((current) => {
              const next = new Set(current);
              if (now) next.add(node);
              else next.delete(node);
              return next;
            })
          }
          onPick={(to) => {
            if (!disabled && to !== here) onChoose(to);
          }}
        />
        {match && hits.length === 0 && <p className="muted chooser-none">No location matches that.</p>}
      </div>

      {name && !exists && (
        <div className="chooser-create">
          <button
            type="button"
            className="picker-option picker-create"
            disabled={busy || disabled}
            onClick={() => void create(null)}
          >
            <Icon name="plus" size={16} />
            {`Add “${name}” and move there`}
          </button>
          {insideNode && (
            <button
              type="button"
              className="picker-option picker-create"
              disabled={busy || disabled}
              onClick={() => void create(insideNode.id)}
            >
              <Icon name="plus" size={16} />
              {`Add “${name}” inside ${pathLabel(insideNode.path)}`}
            </button>
          )}
        </div>
      )}
    </div>
  );
}
